import type { TrendBucket, TrendPoint } from "./api";

/**
 * Pure trend-chart data helpers (F-023). No React, no i18n - unit-testable.
 *
 * The chart never invents hues: it draws from the fixed 7-slot categorical
 * palette below (validated for CVD separation and lightness band), shows at
 * most `maxSlots` groups and folds every remaining group into a gray
 * "Sonstige" series.
 */

/** Fixed categorical palette, 7 validated slots. Order is the slot order. */
export const TREND_PALETTE: readonly string[] = [
  "#2a78d6",
  "#008300",
  "#e87ba4",
  "#eda100",
  "#1baf7a",
  "#eb6834",
  "#4a3aa7",
];

/** The folded rest is gray - deliberately NOT a palette slot. */
export const OTHERS_COLOR = "#6b7280";

/** Series key for rows whose group is null (rendered as "Ohne Zuordnung"). */
export const NULL_GROUP_KEY = "__none__";

/** Series key for the folded remainder (rendered as "Sonstige"). */
export const OTHERS_KEY = "__others__";

export interface TrendSeries {
  /** Entity key: groupId, NULL_GROUP_KEY, or OTHERS_KEY. */
  key: string;
  /** Display name from the API; null for the null group (i18n at render). */
  name: string | null;
  /** True only for the folded "Sonstige" remainder. */
  isOthers: boolean;
  /** Hex fill; a palette slot for real groups, OTHERS_COLOR for the rest. */
  color: string;
  /** Counts aligned index-by-index with TrendMatrix.buckets (0-filled). */
  values: number[];
  /** Sum over all buckets (the top-N ranking key). */
  total: number;
}

export interface TrendMatrix {
  /** Distinct bucketStart values, ascending (ISO strings sort correctly). */
  buckets: string[];
  /** Display order: total desc; "Sonstige" always last. */
  series: TrendSeries[];
  /**
   * Entity-key -> palette-slot map for THIS dataset. Feed it back as
   * `previousSlots` when the same grouping dimension is re-queried (e.g. a
   * changed time window) so surviving groups keep their color.
   */
  slots: ReadonlyMap<string, number>;
}

/**
 * Pivot flat trend rows into a bucket x series matrix.
 *
 * Ranking: groups sort by total count desc (tiebreak: name asc, null last);
 * the top `maxSlots` stay, everything after folds into "Sonstige" (per-bucket
 * sums). "Sonstige" only appears when at least one group actually folded.
 *
 * Color rule (color follows the entity, never its rank): each visible group
 * owns one palette slot. A group present in `previousSlots` (the map returned
 * for the previous dataset of the SAME grouping dimension) keeps that slot;
 * new groups take the lowest free slot in display order. Slots of groups that
 * dropped out of the visible top are freed for newcomers, so stability is
 * guaranteed exactly for groups continuously visible across dataset changes.
 * "Sonstige" never occupies a slot (fixed gray).
 */
export function foldGroups(
  rows: readonly TrendPoint[],
  maxSlots = 6,
  previousSlots?: ReadonlyMap<string, number>,
): TrendMatrix {
  // Never more visible groups than palette slots ("Sonstige" is not a slot).
  const slotCount = Math.min(maxSlots, TREND_PALETTE.length);

  const buckets = [...new Set(rows.map((r) => r.bucketStart))].sort();
  const bucketIndex = new Map(buckets.map((b, i) => [b, i]));

  interface Acc {
    key: string;
    name: string | null;
    values: number[];
    total: number;
  }
  const groups = new Map<string, Acc>();
  for (const row of rows) {
    const key = row.groupId ?? NULL_GROUP_KEY;
    let acc = groups.get(key);
    if (!acc) {
      acc = {
        key,
        name: row.groupName,
        values: new Array<number>(buckets.length).fill(0),
        total: 0,
      };
      groups.set(key, acc);
    }
    const at = bucketIndex.get(row.bucketStart) ?? 0;
    acc.values[at] = (acc.values[at] ?? 0) + row.count;
    acc.total += row.count;
  }

  const ranked = [...groups.values()].sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    if (a.name === null) return 1;
    if (b.name === null) return -1;
    return a.name.localeCompare(b.name, "de");
  });

  const top = ranked.slice(0, slotCount);
  const rest = ranked.slice(slotCount);

  // Slot assignment: survivors keep their previous slot, newcomers take the
  // lowest free slot in display order.
  const slots = new Map<string, number>();
  const taken = new Set<number>();
  for (const acc of top) {
    const prev = previousSlots?.get(acc.key);
    if (prev !== undefined && prev < TREND_PALETTE.length && !taken.has(prev)) {
      slots.set(acc.key, prev);
      taken.add(prev);
    }
  }
  let next = 0;
  for (const acc of top) {
    if (slots.has(acc.key)) continue;
    while (taken.has(next)) next += 1;
    slots.set(acc.key, next);
    taken.add(next);
  }

  const series: TrendSeries[] = top.map((acc) => ({
    key: acc.key,
    name: acc.name,
    isOthers: false,
    color: TREND_PALETTE[slots.get(acc.key) ?? 0] ?? OTHERS_COLOR,
    values: acc.values,
    total: acc.total,
  }));

  if (rest.length > 0) {
    const values = new Array<number>(buckets.length).fill(0);
    let total = 0;
    for (const acc of rest) {
      acc.values.forEach((v, i) => {
        values[i] = (values[i] ?? 0) + v;
      });
      total += acc.total;
    }
    series.push({
      key: OTHERS_KEY,
      name: null,
      isOthers: true,
      color: OTHERS_COLOR,
      values,
      total,
    });
  }

  return { buckets, series, slots };
}

/* ------------------------------------------------------------------------ */
/* German bucket labels (NF-021). date-fns is not installed; the labels are  */
/* derived manually from the ISO date part so they are timezone-stable.      */
/* ------------------------------------------------------------------------ */

// User-facing German month abbreviations (UTF-8 values, ASCII identifiers).
const MONTHS_DE = [
  "Jan",
  "Feb",
  "Mär",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
] as const;

interface Ymd {
  year: number;
  month: number;
  day: number;
}

function parseIsoDate(iso: string): Ymd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/** ISO-8601 week number (the week containing the first Thursday is week 1). */
function isoWeek({ year, month, day }: Ymd): number {
  const date = new Date(Date.UTC(year, month - 1, day));
  const weekday = (date.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  date.setUTCDate(date.getUTCDate() - weekday + 3); // Thursday of this week
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

function twoDigitYear(year: number): string {
  return String(year % 100).padStart(2, "0");
}

/**
 * German axis label for a bucket start: week "KW 12", month "Mär 26",
 * quarter "Q1 26". Unparseable input falls back to the raw string.
 */
export function formatBucketLabel(bucket: TrendBucket, bucketStart: string): string {
  const ymd = parseIsoDate(bucketStart);
  if (!ymd) return bucketStart;
  switch (bucket) {
    case "week":
      return `KW ${isoWeek(ymd)}`;
    case "month":
      return `${MONTHS_DE[ymd.month - 1] ?? "?"} ${twoDigitYear(ymd.year)}`;
    case "quarter":
      return `Q${Math.floor((ymd.month - 1) / 3) + 1} ${twoDigitYear(ymd.year)}`;
  }
}
